import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Mail, Paperclip } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { getBrowserApiBaseUrl } from '@/config/env'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { getMailDetail, resendMailLogMail } from '../services/mailtrapService'
import type { MailDetail, MailDetailModalProps } from '../types'
import { secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'

function formatDateTime(value: string | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

function formatAddressMap(value: unknown): string[] {
  if (!value) return []
  if (Array.isArray(value)) return value.map(String).filter(Boolean)
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).map(([key, val]) =>
      val ? `${key} <${String(val)}>` : key,
    )
  }
  return [String(value)]
}

function attachmentDisplay(
  attachment: unknown,
): { name: string; path?: string } {
  const apiBase = getBrowserApiBaseUrl()
  if (typeof attachment === 'string') {
    const cleanPath = attachment.split('?')[0] ?? attachment
    const name = cleanPath.substring(cleanPath.lastIndexOf('/') + 1) || attachment
    let path: string
    if (attachment.startsWith('http:') || attachment.startsWith('https:')) {
      path = attachment
    } else if (attachment.startsWith('/var/www/html')) {
      const parts = attachment.split('/')
      const storagePath = parts.slice(-2).join('/')
      path = `${apiBase}/storage/${storagePath}`
    } else {
      path = `${apiBase}/storage/${attachment}`
    }
    return { name, path }
  }
  if (attachment && typeof attachment === 'object') {
    const obj = attachment as Record<string, unknown>
    const name =
      (obj.attachment_name as string) ||
      (obj.filename as string)?.split('&signature=')[0] ||
      'attachment'
    const rawPath = (obj.attachment_path as string) || undefined
    let path: string | undefined
    if (rawPath) {
      path = rawPath.startsWith('http') ? rawPath : `${apiBase}/storage/${rawPath}`
    }
    return { name, path }
  }
  return { name: 'attachment' }
}

export function MailDetailModal({
  isOpen,
  mailId,
  onClose,
  onSelectMailId,
}: MailDetailModalProps) {
  const [loading, setLoading] = useState(false)
  const [mail, setMail] = useState<MailDetail | null>(null)
  const [toAddress, setToAddress] = useState('')
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [activeMailId, setActiveMailId] = useState<number | null>(mailId)

  useEffect(() => {
    setActiveMailId(mailId)
  }, [mailId])

  useEffect(() => {
    if (!isOpen || !activeMailId) return
    let cancelled = false
    setLoading(true)
    getMailDetail(activeMailId)
      .then((data) => {
        if (!cancelled) setMail(data)
      })
      .catch((error) => {
        if (!cancelled) showApiError(error)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, activeMailId])

  const iframeSrcDoc = useMemo(() => {
    if (!mail) return ''
    const body = mail.html || mail.body || ''
    return body
      ? `<!DOCTYPE html><html><head><base target="_blank"><meta name="referrer" content="no-referrer"></head><body>${body}</body></html>`
      : ''
  }, [mail])

  const handleSendMail = async () => {
    if (!activeMailId) return
    setSending(true)
    try {
      const msg = await resendMailLogMail(activeMailId, toAddress)
      showSuccess(msg)
      setToAddress('')
      setSendConfirmOpen(false)
    } catch (err) {
      showApiError(err)
    } finally {
      setSending(false)
    }
  }

  const navigateToMail = (id?: number) => {
    if (!id) return
    setActiveMailId(id)
    if (onSelectMailId) onSelectMailId(id)
  }

  const attachments = Array.isArray(mail?.attachments) ? mail.attachments : []
  const fromAddresses = formatAddressMap(mail?.fromAddress || mail?.from)
  const toAddresses = formatAddressMap(mail?.to_address || mail?.recipient || mail?.to)
  const ccAddresses = formatAddressMap(mail?.cc_address)
  const bccAddresses = formatAddressMap(mail?.bcc_address)
  const replyAddresses = formatAddressMap(mail?.reply_address)

  return (
    <>
      <ConfirmDialog
        isOpen={sendConfirmOpen}
        title="Send Mail"
        message={`Send mail to ${toAddress.trim() || 'original recipients'}?`}
        primaryLabel={sending ? 'Sending...' : 'Send'}
        onPrimary={handleSendMail}
        secondaryLabel="Cancel"
        onSecondary={() => setSendConfirmOpen(false)}
        onDismiss={() => setSendConfirmOpen(false)}
      />

      <OnScreenModal
        isOpen={isOpen}
        title={mail?.subject || 'Mail Detail'}
        onClose={onClose}
        maxWidth="max-w-3xl"
      >
        <div className="p-2 space-y-3">
          {/* Header Action Bar: Resend Mail + Prev/Next buttons */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-3">
            <div className="flex items-center gap-2">
              <input
                type="email"
                value={toAddress}
                onChange={(e) => setToAddress(e.target.value)}
                placeholder="To address (optional)"
                className="min-h-[36px] w-56 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15"
              />
              <button
                type="button"
                disabled={sending}
                onClick={() => setSendConfirmOpen(true)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[rgb(21,151,255)] text-white shadow-sm transition hover:bg-[rgb(0,132,230)] disabled:opacity-50"
                title="Send Mail"
                aria-label="Send Mail"
              >
                <Mail className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={!mail?.previous_mail_id}
                onClick={() => navigateToMail(mail?.previous_mail_id)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Previous mail"
                aria-label="Previous mail"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={!mail?.next_mail_id}
                onClick={() => navigateToMail(mail?.next_mail_id)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Next mail"
                aria-label="Next mail"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-slate-500 py-6 text-center">Loading mail...</p>
          ) : !mail ? (
            <p className="text-sm text-slate-500 py-6 text-center">No data.</p>
          ) : (
            <>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm border border-slate-200 rounded p-3 bg-slate-50">
                {fromAddresses.length > 0 && (
                  <>
                    <span className="text-slate-500 font-medium">From:</span>
                    <span className="text-slate-800">{fromAddresses.join(', ')}</span>
                  </>
                )}
                {toAddresses.length > 0 && (
                  <>
                    <span className="text-slate-500 font-medium">To:</span>
                    <span className="text-slate-800">{toAddresses.join(', ')}</span>
                  </>
                )}
                {ccAddresses.length > 0 && (
                  <>
                    <span className="text-slate-500 font-medium">CC:</span>
                    <span className="text-slate-800">{ccAddresses.join(', ')}</span>
                  </>
                )}
                {bccAddresses.length > 0 && (
                  <>
                    <span className="text-slate-500 font-medium">BCC:</span>
                    <span className="text-slate-800">{bccAddresses.join(', ')}</span>
                  </>
                )}
                {replyAddresses.length > 0 && (
                  <>
                    <span className="text-slate-500 font-medium">Reply To:</span>
                    <span className="text-slate-800">{replyAddresses.join(', ')}</span>
                  </>
                )}
                {mail.subject && (
                  <>
                    <span className="text-slate-500 font-medium">Subject:</span>
                    <span className="text-slate-800 font-medium">{mail.subject}</span>
                  </>
                )}
                {mail.created_at && (
                  <>
                    <span className="text-slate-500 font-medium">Date:</span>
                    <span className="text-slate-800">{formatDateTime(mail.created_at)}</span>
                  </>
                )}
                {mail.status && (
                  <>
                    <span className="text-slate-500 font-medium">Status:</span>
                    <span className="text-slate-800">{mail.status}</span>
                  </>
                )}
              </div>

              {attachments.length > 0 && (
                <div className="border border-slate-200 rounded p-3 bg-white">
                  <p className="text-xs font-semibold text-slate-600 mb-2 uppercase">
                    Attachments ({attachments.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((attachment, i) => {
                      const { name, path } = attachmentDisplay(attachment)
                      return path ? (
                        <a
                          key={i}
                          href={path}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={name}
                          className="inline-flex items-center gap-1 rounded bg-[#1597ff]/10 px-2 py-1 text-xs text-[#0b5c9e] hover:bg-[#1597ff]/20"
                        >
                          <Paperclip className="h-3 w-3" /> {name}
                        </a>
                      ) : (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded"
                        >
                          <Paperclip className="h-3 w-3" /> {name}
                        </span>
                      )
                    })}
                  </div>
                </div>
              )}

              {iframeSrcDoc ? (
                <div className="border border-slate-200 rounded overflow-hidden bg-white">
                  {/* Sandboxed iframe — third-party HTML email rendered safely. */}
                  <iframe
                    srcDoc={iframeSrcDoc}
                    sandbox="allow-popups allow-popups-to-escape-sandbox"
                    title="Mail body"
                    className="w-full h-[480px] border-0"
                  />
                </div>
              ) : (
                <p className="text-sm text-slate-500 py-4 text-center">
                  (No HTML body recorded for this mail.)
                </p>
              )}
            </>
          )}

          <div className="flex justify-end pt-3 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className={secondaryEntityActionButtonClass}
            >
              Close
            </button>
          </div>
        </div>
      </OnScreenModal>
    </>
  )
}
